import type { RinkGeometry } from "../geometry/rink";
import type { Performance, Markings, MarkingElement, Highlight } from "../data/schema";
import type { Frame } from "../engine/ChoreoEngine";

const NS = "http://www.w3.org/2000/svg";
const DEFAULT_MARK = "#9fb6c4";
const LABEL_COL = "#cdd8e4"; // higher-contrast coordinate labels
const TRI = 6.5; // px — triangle fits within ~16x16 at full size

function el<K extends keyof SVGElementTagNameMap>(
  tag: K, attrs: Record<string, string | number>, parent: SVGElement
): SVGElementTagNameMap[K] {
  const e = document.createElementNS(NS, tag);
  for (const k in attrs) e.setAttribute(k, String(attrs[k]));
  parent.appendChild(e);
  return e;
}
function colLetters(i: number): string {
  let s = ""; i += 1;
  while (i > 0) { const r = (i - 1) % 26; s = String.fromCharCode(65 + r) + s; i = Math.floor((i - 1) / 26); }
  return s;
}
const HEART = "M0 6 C -8 -2 -9 -9 -4 -10.5 C -1.4 -11.2 0 -9 0 -7 C 0 -9 1.4 -11.2 4 -10.5 C 9 -9 8 -2 0 6 Z";

export class Scene {
  private markingsG: SVGGElement;
  private overlayG: SVGGElement;
  private skaterG: SVGGElement;
  private nodes: Record<string, { g: SVGGElement; rot: SVGGElement; icon: SVGPathElement; label: SVGTextElement }> = {};
  private curMarks = "";
  private gridSpacing = 0;
  private tip: HTMLDivElement;

  constructor(private svg: SVGSVGElement, private geo: RinkGeometry, private perf: Performance, private markings: Markings) {
    svg.setAttribute("viewBox", `0 0 ${geo.width} ${geo.height}`);
    const r = geo.rink;

    // clip path matching the rounded rink, so gridlines don't bleed past the ice / into the corners
    const defs = el("defs", {}, svg);
    const clip = el("clipPath", { id: "rinkClip" }, defs);
    el("rect", { x: geo.tx(0), y: geo.ty(r.widthM), width: geo.s(r.lengthM), height: geo.s(r.widthM), rx: geo.s(r.cornerRadiusM), ry: geo.s(r.cornerRadiusM) }, clip);

    el("rect", { x: geo.tx(0), y: geo.ty(r.widthM), width: geo.s(r.lengthM), height: geo.s(r.widthM), rx: geo.s(r.cornerRadiusM), ry: geo.s(r.cornerRadiusM), fill: "#f4f9fc", stroke: "#9fb2bf", "stroke-width": 2 }, svg);
    el("text", { x: geo.tx(r.lengthM / 2), y: 13, "text-anchor": "middle", fill: "#aebdcc", "font-size": 12, "font-weight": 700, "letter-spacing": 3 }, svg).textContent = "JUDGES";

    this.markingsG = el("g", {}, svg);
    this.overlayG = el("g", {}, svg);
    this.skaterG = el("g", {}, svg);

    const triPts = `8,0 ${-0.9 * TRI},${0.95 * TRI} ${-0.9 * TRI},${-0.95 * TRI}`;
    for (const s of perf.skaters) {
      const g = el("g", {}, this.skaterG);
      const rot = el("g", {}, g);
      el("polygon", { points: triPts, fill: s.color, stroke: "#fff", "stroke-width": 1.3, "stroke-linejoin": "round" }, rot);
      const icon = el("path", { d: HEART, fill: s.color, stroke: "#fff", "stroke-width": 1.2, transform: `scale(${TRI / 6})`, style: "display:none" }, g);
      const label = el("text", { y: TRI * 1.9 + 8, "text-anchor": "middle", "font-size": 11, "font-weight": 700, fill: s.color, stroke: "#fff", "stroke-width": 2, "paint-order": "stroke", style: "font-family: cursive; letter-spacing: -0.02em;" }, g);
      label.textContent = s.name;
      this.nodes[s.id] = { g, rot, icon, label };
    }

    // coordinate hover tooltip (only while the grid layout is active)
    this.tip = document.createElement("div");
    this.tip.style.cssText = "position:fixed;display:none;pointer-events:none;z-index:9999;background:#0d3a52;color:#bfe7fb;border:1px solid #4fc3f7;border-radius:6px;padding:2px 7px;font:600 12px system-ui;";
    document.body.appendChild(this.tip);
    svg.addEventListener("mousemove", (e) => this.onHover(e));
    svg.addEventListener("mouseleave", () => (this.tip.style.display = "none"));

    this.setMarkings(perf.meta.defaultMarkings ?? Object.keys(markings)[0]);
  }

  markingNames(): string[] { return Object.keys(this.markings); }
  setNamesVisible(on: boolean) { for (const id in this.nodes) this.nodes[id].label.style.display = on ? "" : "none"; }

  private onHover(e: MouseEvent) {
    if (this.curMarks !== "grid" || !this.gridSpacing) { this.tip.style.display = "none"; return; }
    const { geo } = this; const r = this.svg.getBoundingClientRect();
    const vx = ((e.clientX - r.left) / r.width) * geo.width;
    const vy = ((e.clientY - r.top) / r.height) * geo.height;
    const xm = geo.mx(vx), ym = geo.my(vy);
    if (xm < 0 || xm > geo.rink.lengthM || ym < 0 || ym > geo.rink.widthM) { this.tip.style.display = "none"; return; }
    const col = colLetters(Math.floor(xm / this.gridSpacing));
    const row = Math.floor((geo.rink.widthM - ym) / this.gridSpacing) + 1; // counts down from the top
    this.tip.textContent = col + row;
    this.tip.style.left = e.clientX + 12 + "px";
    this.tip.style.top = e.clientY + 12 + "px";
    this.tip.style.display = "";
  }

  setMarkings(name: string) {
    this.curMarks = name; this.gridSpacing = 0;
    this.markingsG.innerHTML = "";
    const set = this.markings[name];
    if (!set) return;
    const { geo } = this;
    for (const m of set.elements as MarkingElement[]) {
      const col = (m as { color?: string }).color ?? DEFAULT_MARK;
      if (m.type === "line")
        el("line", { x1: geo.tx(m.from[0]), y1: geo.ty(m.from[1]), x2: geo.tx(m.to[0]), y2: geo.ty(m.to[1]), stroke: col, "stroke-width": 1.5, opacity: 0.6 }, this.markingsG);
      else if (m.type === "circle")
        el("circle", { cx: geo.tx(m.center[0]), cy: geo.ty(m.center[1]), r: geo.s(m.rM), fill: "none", stroke: col, "stroke-width": 1.4, opacity: 0.55 }, this.markingsG);
      else if (m.type === "dot")
        el("circle", { cx: geo.tx(m.center[0]), cy: geo.ty(m.center[1]), r: geo.s(m.rM), fill: col, opacity: 0.65 }, this.markingsG);
      else if (m.type === "grid") { this.gridSpacing = m.spacingM; this.drawGrid(m.spacingM, col); }
    }
  }

  private drawGrid(spacing: number, col: string) {
    const { geo } = this;
    const L = geo.rink.lengthM, W = geo.rink.widthM;
    const nCols = Math.round(L / spacing), nRows = Math.round(W / spacing);
    // gridlines, clipped to the rink shape (hides corner bleed / lines outside the ice)
    const lines = el("g", { "clip-path": "url(#rinkClip)" }, this.markingsG);
    for (let i = 0; i <= nCols; i++)
      el("line", { x1: geo.tx(i * spacing), y1: geo.ty(0), x2: geo.tx(i * spacing), y2: geo.ty(W), stroke: col, "stroke-width": 0.5, opacity: 0.3 }, lines);
    for (let j = 0; j <= nRows; j++)
      el("line", { x1: geo.tx(0), y1: geo.ty(j * spacing), x2: geo.tx(L), y2: geo.ty(j * spacing), stroke: col, "stroke-width": 0.5, opacity: 0.3 }, lines);
    // labels OUTSIDE the gridlines: letters above the top edge, numbers to the left (counting down)
    for (let i = 0; i < nCols; i++)
      el("text", { x: geo.tx((i + 0.5) * spacing), y: geo.ty(W) - 5, "text-anchor": "middle", "font-size": 7.5, "font-weight": 600, fill: LABEL_COL }, this.markingsG).textContent = colLetters(i);
    for (let r = 0; r < nRows; r++)
      el("text", { x: geo.tx(0) - 5, y: geo.ty(W - (r + 0.5) * spacing) + 2.5, "text-anchor": "end", "font-size": 7.5, "font-weight": 600, fill: LABEL_COL }, this.markingsG).textContent = String(r + 1);
  }

  update(frame: Frame, t: number) {
    const { geo } = this;
    const emph = new Set<string>(); const iconFor: Record<string, boolean> = {};
    for (const h of this.perf.highlights as Highlight[]) {
      if (t >= h.fromSec && t < h.toSec)
        for (const id of h.skaters ?? []) { if (h.effect === "icon") iconFor[id] = true; else emph.add(id); }
    }
    for (const id in this.nodes) {
      const p = frame.pos[id]; if (!p) continue;
      const n = this.nodes[id];
      n.g.setAttribute("transform", `translate(${geo.tx(p[0])},${geo.ty(p[1])})`);
      const scale = emph.has(id) ? 1.35 : 1;
      n.rot.setAttribute("transform", `rotate(${-(frame.facing[id] ?? 90)}) scale(${scale})`);
      const showIcon = !!iconFor[id];
      n.icon.style.display = showIcon ? "" : "none";
      n.rot.style.display = showIcon ? "none" : "";
    }
    this.overlayG.innerHTML = "";
    for (const ov of this.perf.overlays) {
      if (!(t >= ov.fromSec && t < ov.toSec)) continue;
      for (const line of ov.lines ?? []) this.drawConnector(line, frame, false);
      for (const ring of ov.circles ?? []) this.drawConnector(ring, frame, true);
    }
  }

  private drawConnector(grp: string[], frame: Frame, closed: boolean) {
    const { geo } = this;
    const pts = grp.filter((id) => frame.pos[id]).map((id) => `${geo.tx(frame.pos[id][0])},${geo.ty(frame.pos[id][1])}`);
    if (pts.length < 2) return;
    el(closed ? "polygon" : "polyline", { points: pts.join(" "), fill: "none", stroke: "#54657a", "stroke-width": 1.6, opacity: 0.7 }, this.overlayG);
  }
}
